/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from "yup";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { AssetMainType, IAssetInfo } from "../../models/asset";
import { useFormik } from "formik";
import { AssetSummary } from "../../commons/assetSummary";
import { Toast } from "primereact/toast";
import { useEffect, useRef } from "react";
import { applyTreasureFieldEdit, freshTreasureLink, TreasureDraft } from "../../commons/treasure-field-link";
import AssetSearchSelector from "../AssetSearchSelector";
import View from "../Container";
import Text from "../Text";
import Label from "../Label";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import Button from "../Button";
import ButtonGroup from "../ButtonGroup";
import { useExchangeRateByType } from "../../hooks/useExchangeRateByType";
import { useTreasureUnitValue } from "../../hooks/useTreasureUnitValue";
import Show from "../Show";
import {
    assetSummaryTypeToAnalytics,
    trackWithdrawCompleted,
    trackWithdrawFailed,
    trackWithdrawSubmitted,
    Tracker,
} from "../../analytics";
import { useHomeContext } from "../../contexts/HomeContext";

interface IWithdrawForm {
    selectedAsset?: AssetSummary;
    date?: Date;
    amount?: number;
    withdrawType?: number;
    quantity?: number;
    unitValue?: number | null;
}

/** Tesouro (exceto Reserva) é marcado por títulos × PU: o resgate parcial precisa do PU para baixar a quantidade. */
const isTreasureByQuantity = (asset?: AssetSummary) =>
    asset?.type === AssetMainType.TreasureBound && !!asset.treasureId && !asset.isTesouroReserva;

/** Tesouro negocia em frações de 0,01 título: resgate que deixa menos que isso, ou leva todo o saldo, é total. */
const TREASURE_MIN_QUANTITY = 0.01;

const treasureWithdrawCoversBalance = (form: IWithdrawForm) => {
    const asset = form.selectedAsset;
    if (asset?.type !== AssetMainType.TreasureBound || form.withdrawType == 1) {
        return false;
    }
    const balance = asset.totalValue ?? 0;
    const held = asset.quantity ?? 0;
    const amount = form.amount ?? 0;
    const quantity = form.quantity ?? 0;
    return (balance > 0 && amount >= balance - 0.01)
        || (held > 0 && quantity > 0 && held - quantity < TREASURE_MIN_QUANTITY);
};

interface IWithdrawHandleFormProps {
    onSuccess: () => void;
}

const WithdrawHandleForm = ({ onSuccess }: IWithdrawHandleFormProps) => {
    const http = useAuthHttp();
    const homeContext = useHomeContext();
    const toast = useRef<Toast | null>(null);
    const chargeMutation = useMutation({
        mutationFn: async (form: IWithdrawForm) => {
            const redeemTotal = form.withdrawType == 1 || treasureWithdrawCoversBalance(form);
            const body = {
                event_date: moment(form.date).format("YYYY-MM-DD HH:mm:ss"),
                event_value: form.amount,
                event_quantity: form.quantity,
                event_type: redeemTotal ? "REDEEM" : "WITHDRAW",
                event_redeem_total: redeemTotal,
                ...(isTreasureByQuantity(form.selectedAsset) && form.withdrawType != 1
                    ? { event_unit_value: form.unitValue }
                    : {}),
            };

            return await http.doPatch<any, IAssetInfo>(`asset/${form.selectedAsset?.id}/withdraw`, body);
        }
    });

    const formik = useFormik<IWithdrawForm>({
        initialValues: {
            selectedAsset: undefined,
            date: undefined,
            withdrawType: undefined,
            quantity: undefined,
            amount: undefined,
            unitValue: undefined
        },
        onSubmit: async (values) => {
            await send(values);
        },
        validationSchema: () => Yup
            .object()
            .shape({
                selectedAsset: Yup.object().required("Campo obrigatório"),
                date: Yup.date()
                    .required("Campo obrigatório")
                    .test(
                        "not-before-purchase",
                        "Data do resgate não pode ser anterior à data de compra",
                        function (value) {
                            const purchaseDate = this.parent.selectedAsset?.purchaseDate;
                            if (!value || !purchaseDate) {
                                return true;
                            }
                            return moment(value).startOf("day").isSameOrAfter(moment(purchaseDate).startOf("day"));
                        }
                    ),
                withdrawType: Yup.number().required("Campo obrigatório"),
                quantity: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (
                                selectedAsset?.type === AssetMainType.Cripto
                                || selectedAsset?.type === AssetMainType.Currency
                                || selectedAsset?.type === AssetMainType.InvestimentFunds
                                || selectedAsset?.type === AssetMainType.Stock
                            ) {
                                return schema.required("Campo obrigatório").min(0.0001, "Valor deve ser maior que 0");
                            }


                            return schema;
                        }
                    )
                    .test(
                        "treasure-quantity-within-holdings",
                        "A quantidade não pode ser maior que a da carteira",
                        function (value) {
                            const asset = this.parent.selectedAsset;
                            const held = asset?.quantity;
                            if (!isTreasureByQuantity(asset) || this.parent.withdrawType == 1 || value == null || !(held > 0)) {
                                return true;
                            }
                            return Number(value) <= held + 1e-9;
                        }
                    )
                    .nullable(),
                amount: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (!selectedAsset) {
                                return schema.required("Selecione um ativo");
                            }

                            return schema;
                        }
                    )
                    .required("Campo obrigatório"),
                unitValue: Yup.number()
                    .when(
                        ["selectedAsset", "withdrawType"],
                        ([selectedAsset, withdrawType], schema) => isTreasureByQuantity(selectedAsset) && withdrawType != 1
                            ? schema.required("Informe o preço unitário (PU)").moreThan(0, "Valor deve ser maior que 0")
                            : schema
                    )
                    .nullable()
            })
    });

    const treasureLink = useRef(freshTreasureLink());
    const treasureDraft = useRef({ value: 0, quantity: 0, unit: 0 });
    const echoedQuantity = useRef<number | null>(null);
    const echoedAmount = useRef<number | null>(null);
    treasureDraft.current = {
        value: Number(formik.values.amount ?? 0),
        quantity: Number(formik.values.quantity ?? 0),
        unit: Number(formik.values.unitValue ?? 0)
    };

    function editTreasure(field: "value" | "quantity" | "unit", patch: Partial<TreasureDraft>) {
        if (!isTreasureByQuantity(formik.values.selectedAsset) || formik.values.withdrawType == 1) {
            return;
        }
        const draft: TreasureDraft = {
            ...treasureDraft.current,
            ...patch,
            held: formik.values.selectedAsset?.quantity
        };
        const result = applyTreasureFieldEdit(treasureLink.current, field, draft);
        treasureLink.current = result.link;
        if (patch.value != null) {
            treasureDraft.current.value = patch.value;
        }
        if (patch.unit != null) {
            treasureDraft.current.unit = patch.unit;
        }
        if (result.quantity != null) {
            const typed = patch.quantity;
            const changed = typed == null || Math.abs(result.quantity - typed) > 1e-9;
            treasureDraft.current.quantity = result.quantity;
            if (changed) {
                echoedQuantity.current = result.quantity;
                formik.setFieldValue("quantity", result.quantity);
            }
        }
        if (result.value != null) {
            treasureDraft.current.value = result.value;
            echoedAmount.current = result.value;
            formik.setFieldValue("amount", result.value);
        }
    }

    const treasureUnitValueQuery = useTreasureUnitValue(
        isTreasureByQuantity(formik.values.selectedAsset) ? formik.values.selectedAsset?.treasureId : undefined,
        formik.values.date,
        "sale",
        (unitValue) => {
            formik.setFieldValue("unitValue", unitValue);
            editTreasure("unit", { unit: unitValue ?? 0 });
        }
    );
    const coversWholeBalance = treasureWithdrawCoversBalance(formik.values);

    const { exchangeRateQuery } = useExchangeRateByType(
        formik.values.selectedAsset?.type?.toLowerCase() as AssetMainType, 
        formik.values.date, 
        formik.values.selectedAsset?.name.split("-")[0],
        (cotation: number) => {
            formik.setFieldValue("cotation", cotation);
        }
    );

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const {
        isPre,
        indexCorrelationName,
        indexCorrelation,
        issuingBankName,
        negotiatingBankName,
        dueDate,
        purchaseDate,
        brokerageValue,
        othersValue,
        monthlyCosts,
        quantity,
        yearTax,
        cotation,
        recurringValue,
        pbType
    } = formik.values.selectedAsset ?? {};

    const toCurrency = (value: number) => {
        return Intl.NumberFormat(
            "pt-BR",
            {
                style: "currency",
                currency: "BRL"
            }
        )
            .format(value);
    }

    const infos: { label: string; value: string; }[] = [];
    if (pbType) {
        infos.push({
            label: "Ativo",
            value: `${pbType} ${isPre ? "pré-fixado" : "pós-fixado"}`
        });
    }

    if (indexCorrelation) {
        infos.push({
            label: "Taxa",
            value: `${indexCorrelationName} + ${indexCorrelation}%`
        });
    }

    if (dueDate) {
        infos.push({
            label: "Vencimento",
            value: moment(dueDate).format("DD/MM/YYYY")
        });
    }

    if (purchaseDate) {
        infos.push({
            label: "Compra",
            value: moment(purchaseDate).format("DD/MM/YYYY")
        });
    }

    if (issuingBankName) {
        infos.push({
            label: "Emissor",
            value: issuingBankName
        });
    }

    if (negotiatingBankName) {
        infos.push({
            label: "Corretora",
            value: negotiatingBankName
        });
    }

    if (brokerageValue || othersValue) {
        infos.push({
            label: "Custos Operacionais",
            value: toCurrency((brokerageValue ?? 0) + (othersValue ?? 0))
        });
    }

    if (monthlyCosts) {
        infos.push({
            label: "Custos Mensais",
            value: toCurrency((monthlyCosts ?? 0))
        });
    }

    if (quantity) {
        infos.push({
            label: "Quantidade",
            value: quantity.toString()
        });
    }

    if (yearTax) {
        infos.push({
            label: "Taxa anual",
            value: `${yearTax}%`
        });
    }

    if (recurringValue) {
        infos.push({
            label: "Valor recorrente",
            value: toCurrency((recurringValue ?? 0))
        });
    }

    if (cotation) {
        infos.push({
            label: "Cotação",
            value: toCurrency((cotation ?? 0))
        });
    }

    const send = async (form: IWithdrawForm) => {
        const assetType = assetSummaryTypeToAnalytics(form.selectedAsset?.type);
        if (assetType) {
            trackWithdrawSubmitted(assetType, form.withdrawType === 1);
        }
        try {
            const res = await chargeMutation.mutateAsync(form);

            if ((res?.status ?? 0) > 299 || (res?.status ?? 0) <= 0) {
                throw new Error("Falha ao cadastrar investimento");
            }

            if (assetType) trackWithdrawCompleted(assetType);
            onSuccess();
            await homeContext.refresh();
        } catch (err) {
            if (assetType) trackWithdrawFailed(assetType);
            Tracker.trackError('WithdrawHandleForm', 'validation');
            toast.current?.show({
                severity: "error",
                summary: "Falha ao cadastrar investimento",
                detail: isTreasureByQuantity(form.selectedAsset) && err instanceof Error ? err.message : undefined
            });
        }
    }

    const withdrawType = [
        {
            id: 1,
            name: "Total"
        },
        {
            id: 2,
            name: "Parcial"
        }
    ];

    return (
        <>
            <Toast ref={toast} />

            <AssetSearchSelector
                label="Informe o ativo"
                hasError={!!formik.errors.selectedAsset && formik.touched.selectedAsset}
                error={formik.errors.selectedAsset}
                selected={formik.values.selectedAsset}
                onSelectBank={(asset: AssetSummary | null) => {
                    if (asset != formik.values.selectedAsset) {
                        formik.setFieldValue("quantity", undefined);
                        formik.setFieldValue("unitValue", undefined);
                        formik.setFieldValue("amount", asset?.totalValue ?? 0);
                        treasureLink.current = freshTreasureLink();
                        formik.setFieldValue("selectedAsset", asset);
                    }
                }}
            />

            <FormField
                id="date"
                label="Data do resgate"
                hasError={!!formik.errors.date && formik.touched.date}
                error={formik.errors.date}
            >
                <Calendar
                    invalid={!!formik.errors.date && formik.touched.date}
                    value={formik.values.date}
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                    showIcon
                    minDate={formik.values.selectedAsset?.purchaseDate
                        ? moment(formik.values.selectedAsset.purchaseDate).toDate()
                        : undefined}
                    maxDate={new Date()}
                    disabledDays={[0, 6]}
                    icon={exchangeRateQuery.isLoading ? "pi pi-spin pi-spinner" : "pi pi-calendar"}
                    onChange={(event) => {
                        formik.setFieldValue("date", event.value)
                    }}
                    locale="pt"
                />
            </FormField>

            <View flexColumn>
                <Text>
                    Valor do resgate
                </Text>

                <View
                    className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                >
                    <ButtonGroup
                        invalid={!!formik.errors.withdrawType && formik.touched.withdrawType}
                        group={withdrawType.map(g => (
                            {
                                name: g.name,
                                disabled: !formik.values.selectedAsset,
                                selected: formik.values.withdrawType == g.id,
                                onClick: () => {
                                    if (g.id == 1) {
                                        formik.setFieldValue("quantity", formik.values.selectedAsset?.quantity ?? 0);
                                        formik.setFieldValue("amount", formik.values.selectedAsset?.totalValue ?? 0);
                                    }

                                    formik.setFieldValue("withdrawType", g.id);
                                }
                            })) ?? []
                        }
                    />
                </View>

                {
                    !!formik.errors.withdrawType && formik.touched.withdrawType && (
                        <Text className="text-red-500 mt-2" size="xs">{formik.errors.withdrawType}</Text>
                    )
                }
            </View>

            <Show when={
                formik.values.selectedAsset?.type === AssetMainType.Cripto
                || formik.values.selectedAsset?.type === AssetMainType.Currency
                || formik.values.selectedAsset?.type === AssetMainType.InvestimentFunds
                || formik.values.selectedAsset?.type === AssetMainType.Stock
            }>
                <FormField
                    id="quantity"
                    hasError={!!formik.errors.quantity && formik.touched.quantity}
                    error={formik.errors.quantity}
                    label={`Quantidade (Máx: ${formik.values.selectedAsset?.quantity ?? 0})`}
                >
                    <InputNumber
                        invalid={!!formik.errors.quantity && formik.touched.quantity}
                        value={formik.values.quantity}
                        disabled={formik.values.withdrawType == 1}
                        max={formik.values.selectedAsset?.quantity ?? 0}
                        min={0.0001}
                        minFractionDigits={2}
                        maxFractionDigits={10}
                        onChange={(e) => {
                            const quantity = e.value ?? 0;

                            if (quantity != formik.values.quantity) {
                                formik.setFieldValue("quantity", quantity);
                            }

                            const cotation = ((formik.values.selectedAsset?.totalValue ?? 0) / (formik.values.selectedAsset?.quantity ?? 0));
                            const amount = cotation * (quantity ?? 0);

                            if (amount != formik.values.amount) {
                                formik.setFieldValue("amount", amount);
                            }
                        }}
                        locale="pt-BR"
                    />
                </FormField>
            </Show>

            <FormField
                id="amount"
                hasError={!!formik.errors.amount && formik.touched.amount}
                error={formik.errors.amount}
                label={`Valor total do resgate`}
            >
                <InputNumber
                    invalid={!!formik.errors.amount && formik.touched.amount}
                    disabled={!!formik.errors.selectedAsset || !formik.values.withdrawType}
                    min={0.0001}
                    onChange={(e) => {
                        const next = e.value ?? 0;
                        if (echoedAmount.current != null) {
                            const echo = echoedAmount.current;
                            echoedAmount.current = null;
                            if (Math.abs(next - echo) < 0.009) {
                                return;
                            }
                        }
                        if (next != formik.values.amount) {
                            formik.setFieldValue("amount", next);
                            if (isTreasureByQuantity(formik.values.selectedAsset)) {
                                editTreasure("value", { value: next });
                            } else if (formik.values.withdrawType != 1) {
                                const quantity = (next * (formik.values.selectedAsset?.quantity ?? 0)) / (formik.values.selectedAsset?.totalValue ?? 0);
                                formik.setFieldValue("quantity", quantity);
                            }
                        }
                    }}
                    value={formik.values.amount}
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                />
            </FormField>

            <Show when={isTreasureByQuantity(formik.values.selectedAsset) && formik.values.withdrawType == 2}>
                <FormField
                    id="unitValue"
                    hasError={!!formik.errors.unitValue && formik.touched.unitValue}
                    error={formik.errors.unitValue}
                    label="Preço unitário (PU) da venda"
                >
                    <InputNumber
                        invalid={!!formik.errors.unitValue && formik.touched.unitValue}
                        value={formik.values.unitValue}
                        onValueChange={(e) => {
                            formik.setFieldValue("unitValue", e.value);
                            editTreasure("unit", { unit: e.value ?? 0 });
                        }}
                        disabled={treasureUnitValueQuery.isFetching}
                        mode="currency"
                        currency="BRL"
                        minFractionDigits={2}
                        maxFractionDigits={8}
                        locale="pt-BR"
                    />
                </FormField>

                <FormField
                    id="treasureQuantity"
                    hasError={!!formik.errors.quantity && formik.touched.quantity}
                    error={formik.errors.quantity}
                    label={`Quantidade de títulos vendidos (em carteira: ${(formik.values.selectedAsset?.quantity ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 6 })})`}
                >
                    <InputNumber
                        value={formik.values.quantity}
                        minFractionDigits={2}
                        maxFractionDigits={6}
                        onValueChange={(e) => {
                            const next = e.value ?? 0;
                            if (echoedQuantity.current != null) {
                                const echo = echoedQuantity.current;
                                echoedQuantity.current = null;
                                if (Math.abs(next - echo) < 1e-9) {
                                    return;
                                }
                            }
                            formik.setFieldValue("quantity", e.value ?? undefined);
                            editTreasure("quantity", { quantity: next });
                        }}
                        locale="pt-BR"
                    />
                </FormField>

                <Text size="xs" className="text-gray-500">
                    Sugerimos o PU de venda do dia. O valor calcula a quantidade uma vez (valor ÷ PU), no máximo a quantidade da carteira. Depois disso, valor e quantidade ficam independentes: a venda pode ser por um preço diferente do PU do dia, acima ou abaixo do saldo. Vendeu todos os títulos? Deixe a quantidade da carteira.
                </Text>
            </Show>

            <Show when={coversWholeBalance}>
                <Text size="xs" className="text-orange-600">
                    Esse resgate cobre todo o saldo do título e será registrado como resgate total do ativo.
                </Text>
            </Show>

            {
                !!infos.length && <View flexColumn gap={2}>
                    <Label className="border-bottom-1 border-gray-300 pb-2">
                        Dados do Investimento
                    </Label>

                    <View className="flex flex-column items-between gap-3">
                        {
                            infos.map(info => {
                                return (
                                    <View key={info.label} className="flex flex-column flex-row justify-between">
                                        <Label>{info.label}</Label>

                                        <Text className="text-xs max-w-xs">{info.value}</Text>
                                    </View>
                                )
                            })
                        }
                    </View>
                </View>
            }

            <View fullWidth>
                <Button
                    onClick={() => {
                        formik.handleSubmit();
                        return Promise.resolve();
                    }}
                    className="w-full justify-content-center"
                    loading={chargeMutation.isPending}
                    disabled={chargeMutation.isPending}
                >
                    Cadastrar
                </Button>
            </View>
        </>
    )
}

export default WithdrawHandleForm;
